import { z } from 'zod';

/**
 * Visible Competitive rank contract (docs/decisions/COMPETITIVE-RANKED.md). Hidden MMR, its
 * tier bounds and difficulty bands stay server-side in `game/ranked-ladder.ts`.
 */

/** Tier display names, lowest first. Placeholders until final names and count are chosen. */
export const RANKED_TIERS = ['Tier 1', 'Tier 2', 'Tier 3', 'Tier 4', 'Tier 5', 'Tier 6'] as const;
export const TOP_TIER = RANKED_TIERS.length - 1;
/** Points that promote a player out of every tier below the top one. */
export const TIER_POINTS = 100;
/** Matches an unranked player plays before receiving a tier. */
export const PLACEMENT_MATCHES = 5;

const MATCH_RESULTS = ['win', 'loss', 'draw'] as const;
export type MatchResult = (typeof MATCH_RESULTS)[number];

const tierSchema = z.number().int().min(0).max(TOP_TIER);

/** A player's visible rank: unranked while placing, then a tier and its points. */
export const rankSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('unranked'), placementPlayed: z.number().int().min(0).max(PLACEMENT_MATCHES) }),
  z.object({ kind: z.literal('ranked'), tier: tierSchema, points: z.number().int().min(0) }),
]);
export type Rank = z.infer<typeof rankSchema>;

export const UNRANKED: Rank = { kind: 'unranked', placementPlayed: 0 };

/** One player's visible change from a ranked match; never carries MMR. */
export const rankChangeSchema = z.object({
  result: z.enum(MATCH_RESULTS),
  before: rankSchema,
  after: rankSchema,
});
export type RankChange = z.infer<typeof rankChangeSchema>;
